<template>
  <transition name="fade" appear>
    <div
      v-if="visible"
      class="panframe"
      :class="wrapperClass ?? 'absolute top-4 left-4'"
      :style="{ width: `${mapWidth}px`, height: `${mapHeight}px` }"
      @mousedown.prevent="handleMapClick"
    >
      <div class="panhandler" :style="viewportRectStyle" @mousedown.prevent="startDrag"></div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = defineProps<{
  visible: boolean;
  mapWidth?: number;
  wrapperClass?: string;
  scrollPosition: { left: number; top: number };
  scrollAreaSize: { width: number; height: number };
  viewportSize: { width: number; height: number };
  onDragScroll: (newPos: { left: number; top: number }) => void;
}>();

const isDragging = ref(false);
let dragStartX = 0;
let dragStartY = 0;
let initialScrollLeft = 0;
let initialScrollTop = 0;

const mapWidth = props.mapWidth ?? 200;

const mapHeight = computed(() => {
  const { width, height } = props.scrollAreaSize;
  if (width === 0 || height === 0) return 0;
  return (height / width) * mapWidth;
});

const viewportRectStyle = computed(() => {
  const sw = props.scrollAreaSize.width;
  const sh = props.scrollAreaSize.height;
  const vw = props.viewportSize.width;
  const vh = props.viewportSize.height;
  const sl = props.scrollPosition.left;
  const st = props.scrollPosition.top;

  const width = (vw / sw) * mapWidth;
  const height = (vh / sh) * mapHeight.value;
  const left = (sl / sw) * mapWidth;
  const top = (st / sh) * mapHeight.value;

  return {
    width: `${width}px`,
    height: `${height}px`,
    left: `${left}px`,
    top: `${top}px`,
  };
});

function startDrag(e: MouseEvent) {
  isDragging.value = true;
  dragStartX = e.clientX;
  dragStartY = e.clientY;
  initialScrollLeft = props.scrollPosition.left;
  initialScrollTop = props.scrollPosition.top;

  window.addEventListener('mousemove', handleDrag);
  window.addEventListener('mouseup', stopDrag);
}

function handleDrag(e: MouseEvent) {
  if (!isDragging.value) return;

  const dx = e.clientX - dragStartX;
  const dy = e.clientY - dragStartY;

  const ratioX = props.scrollAreaSize.width / mapWidth;
  const ratioY = props.scrollAreaSize.height / mapHeight.value;

  let newLeft = initialScrollLeft + dx * ratioX;
  let newTop = initialScrollTop + dy * ratioY;

  // Clamp scroll positions within scrollable bounds
  const maxLeft = props.scrollAreaSize.width - props.viewportSize.width;
  const maxTop = props.scrollAreaSize.height - props.viewportSize.height;

  newLeft = Math.max(0, Math.min(newLeft, maxLeft));
  newTop = Math.max(0, Math.min(newTop, maxTop));

  props.onDragScroll?.({ left: newLeft, top: newTop });
}

function handleMapClick(e: MouseEvent) {
  if (!props.scrollAreaSize || !props.viewportSize) return;

  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const offsetX = e.clientX - rect.left;
  const offsetY = e.clientY - rect.top;

  // calculates ratio towards scroll position
  const ratioX = props.scrollAreaSize.width / mapWidth;
  const ratioY = props.scrollAreaSize.height / mapHeight.value;

  let newLeft = offsetX * ratioX - props.viewportSize.width / 2;
  let newTop = offsetY * ratioY - props.viewportSize.height / 2;

  // limit to max scrollable position
  const maxLeft = props.scrollAreaSize.width - props.viewportSize.width;
  const maxTop = props.scrollAreaSize.height - props.viewportSize.height;

  newLeft = Math.max(0, Math.min(newLeft, maxLeft));
  newTop = Math.max(0, Math.min(newTop, maxTop));

  props.onDragScroll?.({ left: newLeft, top: newTop });
}

function stopDrag() {
  isDragging.value = false;
  window.removeEventListener('mousemove', handleDrag);
  window.removeEventListener('mouseup', stopDrag);
}
</script>

<style scoped lang="css">
@config "../../../tailwind.config.js";
@reference "tailwindcss";
@reference "tailwindcss/utilities";
@layer app {
  .panframe {
    @apply pointer-events-auto border border-white bg-black/50;
  }

  .panhandler {
    @apply absolute border border-white bg-white/70 cursor-pointer pointer-events-auto;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.8s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
  }

  .bg-opacity-50 {
    opacity: 0.5;
  }

  .bg-opacity-70 {
    opacity: 0.7;
  }
}
</style>
